/*
 * @providesModule i18nextInstance
 *
 */

import i18next, {ModuleType} from 'i18next';
import {initReactI18next} from 'react-i18next';
import numeral from 'numeral';
import moment, {isDate} from 'moment';
import {NativeModules, Platform} from 'react-native';

const DEFAULT_LNG = 'en';

const getLanguageFromLocale = (local?: string): string => {
  return local?.split('_')?.[0]?.split('-')[0] ?? DEFAULT_LNG;
};

const languageDetector = {
  type: 'languageDetector' as ModuleType,
  async: true,
  detect: (cb: Function) =>
    cb(
      getLanguageFromLocale(
        Platform.OS === 'ios'
          ? NativeModules.SettingsManager.settings.AppleLocale ||
          NativeModules.SettingsManager.settings.AppleLanguages[0] // iOS 13
          : NativeModules.I18nManager.localeIdentifier,
      ),
    ),
  init: () => {},
  cacheUserLanguage: () => {},
};


const i18nextInstance = i18next.createInstance();

//add listener if local has changed
i18nextInstance.on('languageChanged', lng => {
  if (numeral.locales.hasOwnProperty(lng)) {
    // if local exist
    numeral.locale(lng);
  } else {
    console.warn('No locale set up for numeral for ' + lng);
    //default to english
    numeral.locale(DEFAULT_LNG);
  }

  if (moment.locales().includes(lng)) {
    //if local exist
    moment.locale(lng);
  } else {
    console.warn('No locale set up for moment for ' + lng);
    //default to english
    moment.locale(DEFAULT_LNG);
  }
});

i18nextInstance
  .use(languageDetector)
  .use(initReactI18next)
  .init(
    {
      compatibilityJSON: 'v3', // compatibility v3 as new format not compatible with Android
      simplifyPluralSuffix: false, //don't use _plural but use the numbers instead
      react: {
        useSuspense: false,
      },
      fallbackLng: DEFAULT_LNG, //fallback language
      // lng: 'en', //default language no needed with language detector
      debug: __DEV__,
      resources: {
        en: {
          translations: require('./Locales/en.json'),
          moment: require('moment/locale/en-gb'),
          numeral: require('numeral/locales/en-gb'),
        },
        fr: {
          translations: require('./Locales/fr.json'),
          moment: require('moment/locale/fr'),
          numeral: require('numeral/locales/fr'),
        },
      },
      ns: ['translations'],
      defaultNS: 'translations',
      interpolation: {
        escapeValue: false, //react already escaping, so not needed
        format: function (value, format, _lng) {
          if (format === 'uppercase') {
            if (typeof value === 'string') {
              return value.toUpperCase();
            } else {
              return value;
            }
          }
          if (format === 'lowercase') {
            if (typeof value === 'string') {
              return value.toLowerCase();
            } else {
              return value;
            }
          }
          if (format === 'ordinal') {
            return numeral(value).format('0o');
          }

          //Date formatting in translator
          if (isDate(value)) {
            return moment(value).format(format);
          }

          return value;
        },
      },
    },
  ).then(() => {
    //add rules for English to handle zero (by default only handle 1 or plurals, making 0 go to plural)
    i18next.services.pluralResolver?.addRule('en', {
      name: 'English',
      numbers: [0, 1, 2],
      plurals: function (n: number) {
        return Number(n === 0 ? 0 : n === 1 ? 1 : 2);
      },
    });
    i18next.services.pluralResolver?.addRule('fr', {
      name: 'Français',
      numbers: [0, 1, 2],
      plurals: function (n: number) {
        return Number(n === 0 ? 0 : n === 1 ? 1 : 2);
      },
    });
});

export default i18nextInstance;
